import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import {
  DataType,
  FHIRHelper,
  IFilter,
  IPatientUser,
  IRewardScore,
  IWidgetPatientParameter,
  PreferenceContext,
  REWARD_STATUS,
  RewardDefinition,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { takeUntil } from "rxjs/operators";
import { HelpData } from "src/app/helpers/helpData";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { RewardDefinitionService } from "src/app/providers/rewardDefinition.service";
import { RewardScoreService } from "src/app/providers/rewardScore.service";
import { SessionService } from "src/app/providers/session.service";
import { WidgetBaseComponent } from "../base/widget-base/widget-base.component";
import { Item } from "../item-selector/item-selector.component";
import { WidgetActionConfig } from "../widget-actions/widget-actions.component";
import { PatientRewardDataSource } from "./patient-reward-datasource";
import { RewardDetailsComponent } from "./reward-details/reward-details.component";

@Component({
  selector: "app-patient-reward",
  templateUrl: "./patient-reward.component.html",
  styleUrls: ["./patient-reward.component.scss", "../base/widget-base/widget-base.component.scss"],
})
export class PatientRewardComponent extends WidgetBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  public actions: WidgetActionConfig[];
  @Input() set patientUser(pu: IPatientUser) {
    if (pu?.user?.caremateIdentifier) {
      this.pu = pu;
      this.patientServiceId = pu.patient?.healthcareservice?.[0]?.reference;
      this.loadDataSource();
      this.loadPreferences();
    }
  }
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<IRewardScore>;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("focus") target: ElementRef;
  public displayedColumns: string[] = [
    "objectifName",
    "objectifStart",
    "objectifEnd",
    "objectifProgress",
    // 'rewardStatut',
    // 'rewardHistory'
    "status",
    "action",
  ];
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match

  public dataSource: PatientRewardDataSource;
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  private currentPageSize: number;
  public sliderData: Item[] = [];
  public showDraft = true;
  public pu: IPatientUser;
  public filterFormTable = this.fb.group({
    fromDate: new UntypedFormControl(moment().subtract(3, "month"), {
      validators: Validators.required,
      updateOn: "blur",
    }),
    toDate: new UntypedFormControl(moment().endOf("day"), {
      validators: Validators.required,
      updateOn: "blur",
    }),
  });
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public globalSearchValue = "";
  public loading = true;
  public maxFromDate: string;
  public minToDate: string;
  public today = moment();
  public allDefinitions: RewardDefinition[];
  public refreshSub = this.sessionService.refreshRewardsList.subscribe(() => {
    this.loadDataSource();
  });
  private get from() {
    return this.filterFormTable.get("fromDate")?.value as string;
  }
  private get to() {
    return this.filterFormTable.get("toDate")?.value as string;
  }
  public patientServiceId: string;
  public lastFilter: IFilter;
  public REWARD_STATUS = REWARD_STATUS;

  constructor(
    private preferenceService: PreferenceService,
    private sessionService: SessionService,
    protected rewardScoreService: RewardScoreService,
    private translateService: TranslateService,
    private fb: UntypedFormBuilder,
    private responsiveDialog: ResponsiveDialogService,
    private dialog: MatDialog,
    protected rewardDefinitionService: RewardDefinitionService,
    private helpData: HelpData,
    protected responsiveService: ResponsiveService
  ) {
    super(responsiveService);
  }

  ngOnInit(): void {
    this.createDataSource();
    this.filterFormTable.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.updateDate());
  }

  ngAfterViewInit(): void {
    this.initPaginatorAndSort();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.dataSource.clear();
    this.refreshSub.unsubscribe();
  }

  private updateDate() {
    const from = moment(this.from).format("YYYY-MM-DDTHH:mm:ss");
    const to = moment(this.to).format("YYYY-MM-DDTHH:mm:ss");
    this.maxFromDate = moment(to).toISOString();
    this.minToDate = moment(from).toISOString();
    if (from && to && !this.dataSource.loading) {
      this.dataSource.loadRewardsPage(from, to, false, this.pu.user.caremateIdentifier, this.preferenceService.getAutoRefreshWidget());
    }
  }

  private initPaginatorAndSort() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;

    // Detect page size change
    this.currentPageSize = this.paginator.pageSize;
    this.paginator.page.pipe(takeUntil(this.onDestroy$)).subscribe((page) => {
      if (this.currentPageSize !== page.pageSize) {
        this.updatePreference();
      }
      this.currentPageSize = this.paginator.pageSize;
    });
  }

  private updatePreference() {
    this.preferenceService
      .update({
        context: PreferenceContext.PATIENT_REWARD_LIST,
        parameters: {
          filters: this.dataSource.getAllFilters(),
        } as IWidgetPatientParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  private loadDataSource() {
    this.createDataSource();
    this.dataSource.loadRewardsPage(
      moment(this.from).format("YYYY-MM-DDTHH:mm:ss"),
      moment(this.to).format("YYYY-MM-DDTHH:mm:ss"),
      false,
      this.pu.user.caremateIdentifier,
      this.preferenceService.getAutoRefreshWidget()
    );
  }

  private createDataSource() {
    if (!this.dataSource) {
      this.dataSource = new PatientRewardDataSource(this.rewardScoreService, this.translateService);
      this.rewardDefinitionService
        .listRewardDefinitions()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((res) => {
          this.allDefinitions = res;
          this.dataSource.rewardDefinitions = this.allDefinitions;
        });
    }
  }

  private loadPreferences() {
    this.preferenceService
      .list(PreferenceContext.PATIENT_REWARD_LIST)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((parameters: IWidgetPatientParameter) => {
        if (parameters) {
          // Apply saved filters
          parameters.filters.forEach((filter: IFilter) => {
            this.applyFilter(filter);
          });
        }
      });
  }

  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public applyFilter(filter: IFilter): void {
    this.lastFilter = filter;
    this.dataSource.setFilter(filter);
    this.activeFilters = [...(this.dataSource?.getAllFilters() ?? [])];
  }

  public getTargetName(reference: string): string {
    return this.getTargetDef(reference)?.rewardDisplayName?.[this.translateService.currentLang] || "-";
  }

  public getTargetDescription(reference: string): string {
    return this.getTargetDef(reference)?.dashboardRewardText?.[this.translateService.currentLang] || "-";
  }

  private getTargetDef(reference: string) {
    return this.allDefinitions?.find((d) => FHIRHelper.getMainIdentifier(d)?.value === reference);
  }

  public getTargetValue(score: IRewardScore): string {
    const targetDef = this.getTargetDef(score.rewardDefinitionReference.reference);
    return `${score?.currentScore}/${targetDef?.rewardScore}`;
  }

  public onDetails(reward: IRewardScore, createAction: boolean, description: string, score: string): void {
    this.dialog
      .open(RewardDetailsComponent, {
        data: {
          reward,
          createAction,
          description,
          score,
          patientServiceId: this.patientServiceId,
        },
        maxWidth: createAction ? "80vw" : "30vw",
        maxHeight: "93vh",
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.applyFilter(this.lastFilter);
        this.loadDataSource();
      });
  }

  public openRewardHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.patientRewardHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }
}
